// Офферы: свои (с чем работаем) и каталог сети. Каталог buyo обновляется каждый час; по офферам,
// где уже шёл трафик, видно свои лиды и апрув от решённых за выбранный период. Разведка (этап 6): движение
// каталога за неделю, «на лид» (апрув × выплата), апрув за 30 дней по снимкам; разобрать оффер — вопросом агенту.
// Свои офферы (ревью 26.09, «Добавить» 1 и 4): экономика от платформы — прогнозный апрув нового лида, апрув
// по возрасту лида (какая доля лидов без решения ещё станет апрувом), выплата, окупаемый и целевой CPL.
// Запас товара — как в кабинете buyo; новый для нас оффер с низким запасом не тестируем (правило владельца).
import { Button, Card, Chip, EmptyState, pct, Skeleton, Sparkline, toast, usd, when } from '@autoaff/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  ArrowUpDown,
  MessageCircle,
  Package,
  PlugZap,
  Plus,
  Search,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { api } from '../api';
import {
  type MyOffer,
  money,
  type NetworkOffer,
  type OfferEconomics,
  type OffersData,
  type ScoutSignal,
  useIntegrations,
} from '../business';
import { chatStore } from '../chat/store';
import {
  FilterBar,
  type FilterOption,
  FilterSelect,
  PeriodPicker,
  periodKey,
  periodPhrase,
  ResetFilters,
  usePeriod,
  useRemembered,
} from '../filters';
import { OfferTabs, PageHead } from './common';

const isNew = (o: NetworkOffer) => Date.now() - new Date(o.firstSeenAt).getTime() < 7 * 86_400_000;

const OFFER_STATUS: Record<string, string> = {
  idea: 'идея',
  testing: 'на тесте',
  active: 'в работе',
  scaling: 'масштабируем',
  paused: 'на паузе',
  archived: 'в архиве',
};

/** Подпись точки ежедневного снимка: «сегодня», «вчера», «12 дн. назад». */
const daysAgo = (n: number) => (n === 0 ? 'сегодня' : n === 1 ? 'вчера' : `${n} дн. назад`);

const STOCK: Record<'low' | 'medium' | 'high', string> = {
  low: 'низкий',
  medium: 'средний',
  high: 'высокий',
};

/** Запас: у нашего оффера низкий — льём, пока товар есть; у нового — не тестируем. */
function StockChip({ o }: { o: NetworkOffer }) {
  if (!o.stock) return null;
  if (o.stock !== 'low') return <Chip>запас {STOCK[o.stock]}</Chip>;
  return o.trend?.ours ? (
    <Chip tone="warning">запас низкий</Chip>
  ) : (
    <Chip tone="danger">низкий запас — не тестируем</Chip>
  );
}

/** Только маркетплейс: трафик на сайт сети с допродажами — владелец льёт только на свой лендинг. */
function MarketplaceChip({ o }: { o: NetworkOffer }) {
  if (o.landing !== false) return null;
  return o.trend?.ours ? (
    <Chip tone="warning">только маркетплейс</Chip>
  ) : (
    <Chip tone="danger">только маркетплейс — не подходит</Chip>
  );
}

const pp = (n: number) => `${n > 0 ? '+' : '−'}${Math.abs(n).toFixed(1)} п.п.`;

/** Разобрать оффер для теста — новый чат с агентом, вопрос уходит сразу. */
function askAbout(o: NetworkOffer) {
  chatStore.setThread(null);
  chatStore.setContext({ kind: 'offer', id: o.extId, label: `Оффер сети № ${o.extId} · ${o.name}` });
  chatStore.open({
    draft: `Разбери оффер сети № ${o.extId} «${o.name}» для теста (offer_scout): динамика апрува и выплаты, сезон, спрос и конкуренты, какие у нас есть креативы и лендинг. Стоит ли тестировать? Если да — предложи заявкой offer.test с планом: лендинг, батч роликов, бюджет, срок и критерий успеха.`,
  });
}

function OfferCard({ o, phrase, own }: { o: NetworkOffer; phrase: string; own: OfferEconomics | null }) {
  const s = o.stats;
  const t = o.trend;
  return (
    <article className="offer-card">
      <div className="offer-head">
        {o.image ? (
          <img className="offer-img" src={o.image} alt="" loading="lazy" />
        ) : (
          <span className="offer-img grid place-items-center muted">
            <Package size={20} />
          </span>
        )}
        <div className="min-w-0">
          <b>{o.name || `Оффер ${o.extId}`}</b>
          <div className="offer-chips">
            {t?.ours && <Chip tone="info">наш · {OFFER_STATUS[t.ours.status] ?? t.ours.status}</Chip>}
            <MarketplaceChip o={o} />
            <StockChip o={o} />
            {o.inSeason && <Chip tone="success">в сезоне</Chip>}
            {isNew(o) && <Chip tone="accent">новый</Chip>}
            {t?.payoutD30 !== null && t?.payoutD30 !== undefined && Math.abs(t.payoutD30) >= 0.1 && (
              <Chip tone={t.payoutD30 > 0 ? 'success' : 'warning'}>
                выплата {t.payoutD30 > 0 ? '+' : '−'}
                {Math.round(Math.abs(t.payoutD30) * 100)}%
              </Chip>
            )}
            <Chip>№ {o.extId}</Chip>
          </div>
        </div>
      </div>
      <div className="offer-metrics offer-metrics-5">
        <div>
          <span>Апрув</span>
          <b>{o.approvePct !== null ? pct(o.approvePct) : '—'}</b>
        </div>
        <div>
          <span>CR</span>
          <b>{o.crPct !== null ? pct(o.crPct) : '—'}</b>
        </div>
        <div>
          <span>Выплата</span>
          <b>{o.payoutUz !== null ? money(o.payoutUz, o.payoutCurrency) : '—'}</b>
        </div>
        <div className="m-wide">
          <span>Цена</span>
          <b>{o.priceUzs ? money(o.priceUzs, 'UZS') : '—'}</b>
        </div>
        <div>
          <span>Холд</span>
          <b>{o.holdDays !== null ? `${o.holdDays} дн.` : '—'}</b>
        </div>
      </div>
      {t && (t.series.length > 1 || t.perLead !== null || own?.breakEvenCpl != null) && (
        <div className="offer-trend">
          <div>
            <span>Апрув за 30 дней</span>
            {t.series.length > 1 ? (
              <Sparkline
                values={t.series}
                tone="muted"
                fromZero={false}
                labels={t.series.map((_, i) => daysAgo(t.series.length - 1 - i))}
                format={(n) => pct(n)}
              />
            ) : (
              <small>история копится</small>
            )}
          </div>
          <div className="offer-trend-nums">
            {t.approveD7 !== null && Math.abs(t.approveD7) >= 0.5 && (
              <span className={t.approveD7 > 0 ? 'pos' : 'neg'}>
                {t.approveD7 > 0 ? <TrendingUp aria-hidden /> : <TrendingDown aria-hidden />}
                {pp(t.approveD7)} за неделю
              </span>
            )}
            {own?.breakEvenCpl !== null && own?.breakEvenCpl !== undefined ? (
              <span
                title={`По вашим лидам: прогнозный апрув × выплата${t.perLead !== null ? `; по цифрам каталога — ${money(t.perLead, o.payoutCurrency)} на лид` : ''}`}
              >
                окупаемый CPL <b className="tnum">{usd(own.breakEvenCpl)}</b>
              </span>
            ) : (
              t.perLead !== null && (
                <span title="Апрув каталога × выплата: сколько в среднем приносит лид по цифрам сети">
                  на лид <b className="tnum">{money(t.perLead, o.payoutCurrency)}</b>
                </span>
              )
            )}
          </div>
        </div>
      )}
      {s.leads > 0 && (
        <div className="offer-foot">
          Ваши лиды {phrase}: <b className="tnum">{s.leads}</b>
          {s.approveRate !== null && <> · {pct(s.approveRate)} от решённых</>}
          {s.revenueUsd > 0 && <> · {usd(s.revenueUsd)}</>}
        </div>
      )}
      <Button size="sm" variant="ghost" className="offer-ask" onClick={() => askAbout(o)}>
        <MessageCircle />
        Разобрать с агентом
      </Button>
    </article>
  );
}

const SIGNAL_ICON: Record<ScoutSignal['kind'], 'up' | 'down' | null> = {
  new: null,
  approve_up: 'up',
  approve_down: 'down',
  payout_up: 'up',
  payout_down: 'down',
  season_in: 'up',
  season_out: 'down',
  stock_down: 'down',
};

/** Движение каталога за неделю: новинки, апрув, выплата, сезон; наши офферы — с пометкой. */
function Movers({ signals, onPick }: { signals: ScoutSignal[]; onPick: (extId: string) => void }) {
  return (
    <Card title="Движение в каталоге" className="movers">
      <ul>
        {signals.map((s) => {
          const dir = SIGNAL_ICON[s.kind];
          return (
            <li key={`${s.extId}:${s.kind}`}>
              <span className={dir === 'up' ? 'pos' : dir === 'down' ? 'neg' : 'accent-ink'} aria-hidden>
                {dir === 'up' ? <TrendingUp /> : dir === 'down' ? <TrendingDown /> : <Package />}
              </span>
              <button type="button" className="link" onClick={() => onPick(s.extId)}>
                {s.name}
              </button>
              {s.ours && <Chip tone="info">наш</Chip>}
              <span className="muted">{s.text}</span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

const PAYOUT_FROM: Record<NonNullable<OfferEconomics['payoutFrom']>, string> = {
  own: 'средняя по вашим апрувам за 30 дней',
  offer: 'ставка оффера',
  overall: 'средняя по всем офферам — своих апрувов мало',
};

/** Какие свои офферы показать в экономике: в работе или с лидами. */
const econShown = (o: MyOffer) =>
  !!o.economics &&
  (['testing', 'active', 'scaling', 'paused'].includes(o.status) ||
    o.economics.mature.leads > 0 ||
    o.stats.leads > 0);

/** Ссылка коротко: домен и путь, длинный номер лендинга — обрезан. */
const shortUrl = (u: string) => {
  try {
    const x = new URL(u);
    const p = `${x.host}${x.pathname}`.replace(/\/$/, '');
    return p.length > 44 ? `${p.slice(0, 43)}…` : p;
  } catch {
    return u;
  }
};

/**
 * Лендинги сети у своего оффера: ссылки на лендинги buyo (русский, узбекский), которые вставляет владелец.
 * Агент открывает их сам и берёт фото и описание товара для своих лендингов и креативов.
 */
function NetworkLandings({ o }: { o: MyOffer }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const list = o.networkLandings ?? [];
  const save = async (urls: string[], done?: string) => {
    setBusy(true);
    try {
      await api(`/api/offers/${o.id}/network-landings`, { urls }, 'PUT');
      await qc.invalidateQueries({ queryKey: ['offers'] });
      if (done) toast(done);
      return true;
    } catch (err) {
      toast((err as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (await save([...list, url.trim()], 'Ссылка добавлена — агент возьмёт оттуда фото и описание товара'))
      setUrl('');
  };
  return (
    <div className="net-landings">
      <span className="net-landings-label">Лендинги сети</span>
      {list.length > 0 ? (
        <div className="net-landings-list">
          {list.map((u) => (
            <div key={u} className="net-landing">
              <a href={u} target="_blank" rel="noopener noreferrer" className="mono" title={u}>
                {shortUrl(u)}
              </a>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Убрать ссылку ${shortUrl(u)}`}
                disabled={busy}
                onClick={() => void save(list.filter((x) => x !== u))}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted">
          Вставьте ссылки на лендинги buyo (русский, узбекский) — агент возьмёт оттуда фото и описание товара.
        </p>
      )}
      <form className="net-landings-form" onSubmit={add}>
        <input
          className="input mono"
          placeholder="https://my.buyo.network/landings/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label={`Ссылка на лендинг сети — ${o.name}`}
          autoComplete="off"
          spellCheck={false}
        />
        <Button
          type="submit"
          size="sm"
          icon={<Plus />}
          disabled={busy || !/^https:\/\/\S+\.\S+/.test(url.trim()) || list.length >= 10}
        >
          Добавить
        </Button>
      </form>
    </div>
  );
}

/**
 * Какие точки кривых апрува по возрасту показать: индекс точки и подпись. Точки — рабочие часы колл-центра
 * (@autoaff/mcp SAME_DAY_AGES_H: 0, 1, 2, 4, 8, 12 ч; LATER_AGES_H: 0, 1, 2, 4, 8 ч, 1…5 рабочих дней).
 */
const SAME_DAY_SHOWN = [
  [0, 'сразу'],
  [2, '2 ч'],
  [4, '8 ч'],
] as const;
const LATER_SHOWN = [
  [0, '0 ч'],
  [3, '4 ч'],
  [5, '1 рабочий день'],
  [6, '2 дня'],
] as const;
const ageText = (xs: number[], shown: ReadonlyArray<readonly [number, string]>) =>
  shown.map(([i, label]) => `${label} ${pct((xs[i] ?? 0) * 100)}`).join(', ');

/**
 * Экономика своих офферов: сколько можно платить за лид (окупаемый CPL) и сколько нужно (целевой, ниже
 * на маржу владельца), прогнозный апрув нового лида и апрув по возрасту — считает платформа.
 */
function OwnEconomics({ mine, margin }: { mine: MyOffer[]; margin: number }) {
  const rows = mine.filter(econShown);
  if (!rows.length) return null;
  return (
    <Card
      title="Ваши офферы"
      className="own-econ"
      action={
        <Link to="/settings" hash="autonomy" className="own-econ-margin">
          маржа {Math.round(margin * 100)}%
        </Link>
      }
    >
      <p className="own-econ-lead">
        Окупаемый CPL — сколько можно платить за лид в ноль: прогнозный апрув × выплата. Целевой — ниже на
        вашу маржу. Апрув по возрасту — какая доля лидов, ещё без решения, станет апрувом.
      </p>
      <ul>
        {rows.map((o) => {
          const e = o.economics as OfferEconomics;
          const ages = e.byAge;
          return (
            <li key={o.id}>
              <div className="own-econ-head">
                <b>{o.name}</b>
                <Chip>{OFFER_STATUS[o.status] ?? o.status}</Chip>
                {e.thin && (
                  <span
                    title={`Решённых лидов ${e.mature.decided} из 20 нужных — цифры подтянуты к ${e.prior === 'catalog' ? 'апруву оффера в каталоге buyo' : 'общим цифрам по всем офферам'}`}
                  >
                    <Chip tone="warning">мало данных</Chip>
                  </span>
                )}
              </div>
              <div className="offer-metrics">
                <div>
                  <span>Апрув нового лида</span>
                  <b>{e.approveProb === null ? '—' : pct(e.approveProb * 100)}</b>
                </div>
                <div title={e.payoutFrom ? PAYOUT_FROM[e.payoutFrom] : undefined}>
                  <span>Выплата</span>
                  <b>{e.payoutUsd === null ? '—' : usd(e.payoutUsd)}</b>
                </div>
                <div>
                  <span>Окупаемый CPL</span>
                  <b>{e.breakEvenCpl === null ? '—' : usd(e.breakEvenCpl)}</b>
                </div>
                <div>
                  <span>Целевой CPL</span>
                  <b>{e.targetCpl === null ? '—' : usd(e.targetCpl)}</b>
                </div>
              </div>
              <p className="own-econ-ages">
                {ages ? (
                  <>
                    Апрув по возрасту лида в рабочих часах колл-центра (08–23): в день заявки —{' '}
                    <span className="tnum">{ageText(ages.sameDay, SAME_DAY_SHOWN)}</span>; ночью и позже —{' '}
                    <span className="tnum">{ageText(ages.later, LATER_SHOWN)}</span>; старше 5 дней не ждём
                  </>
                ) : (
                  'Апрув по возрасту появится, когда у лидов будут решения сети'
                )}
              </p>
              <NetworkLandings o={o} />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

type Sort = 'traffic' | 'perLead' | 'moving' | 'approve' | 'payout' | 'new';
type Show = 'all' | 'landing' | 'traffic' | 'season' | 'new' | 'stocked';

const SORTS: Array<FilterOption<Sort>> = [
  { value: 'traffic', label: 'Ваш трафик' },
  { value: 'perLead', label: 'На лид' },
  { value: 'moving', label: 'В движении' },
  { value: 'approve', label: 'Апрув сети' },
  { value: 'payout', label: 'Выплата' },
  { value: 'new', label: 'Новые в каталоге' },
];
const SORT_KEYS = SORTS.map((x) => x.value);

const SHOW: Array<{ value: Show; label: string; match: (o: NetworkOffer) => boolean }> = [
  { value: 'all', label: 'Все', match: () => true },
  { value: 'landing', label: 'Для лендинга', match: (o) => o.landing !== false },
  { value: 'traffic', label: 'С вашим трафиком', match: (o) => o.stats.leads > 0 },
  { value: 'season', label: 'В сезоне', match: (o) => o.inSeason },
  { value: 'new', label: 'Новые за неделю', match: isNew },
  {
    value: 'stocked',
    label: 'Запас средний и высокий',
    match: (o) => o.stock === 'medium' || o.stock === 'high',
  },
];

/**
 * Порядок каталога. «Ваш трафик» — офферы, где уже шли ваши лиды, затем порядок каталога (сезон, апрув);
 * «на лид» — апрув × выплата; «в движении» — сильнее всего изменился апрув за неделю.
 */
const sortKey: Record<Sort, (o: NetworkOffer) => number> = {
  traffic: (o) => o.stats.leads,
  perLead: (o) => o.trend?.perLead ?? -1,
  moving: (o) => Math.abs(o.trend?.approveD7 ?? 0),
  approve: (o) => o.approvePct ?? -1,
  payout: (o) => o.payoutUz ?? -1,
  new: (o) => Date.parse(o.firstSeenAt),
};

export function OffersPage() {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useRemembered<Sort>('offers:sort', 'traffic', SORT_KEYS);
  const [show, setShow] = useState<Show>('all');
  const [period, setPeriod] = usePeriod('offers', '30d');
  const integ = useIntegrations();
  const q = useQuery({
    queryKey: ['offers', periodKey(period)],
    queryFn: () => api<OffersData>(`/api/offers?period=${periodKey(period)}`),
    placeholderData: keepPreviousData,
  });
  const buyo = integ.data?.buyo;
  const phrase = periodPhrase(period);
  const list = useMemo(() => {
    const all = q.data?.network ?? [];
    const s = search.trim().toLowerCase();
    const match = SHOW.find((x) => x.value === show)?.match ?? (() => true);
    const rows = all.filter((o) => match(o) && (!s || o.name.toLowerCase().includes(s) || o.extId === s));
    const key = sortKey[sort];
    return [...rows].sort((a, b) => key(b) - key(a));
  }, [q.data, search, sort, show]);
  const showOpts = useMemo(() => {
    const all = q.data?.network ?? [];
    return SHOW.map((x) => ({ value: x.value, label: x.label, count: all.filter(x.match).length }));
  }, [q.data]);
  const filtered = show !== 'all' || search.trim() !== '';
  const reset = () => {
    setShow('all');
    setSearch('');
    setSort('traffic');
  };

  return (
    <>
      <PageHead
        title="Офферы"
        sub={
          q.data?.fetchedAt
            ? `Каталог buyo обновлён ${when(q.data.fetchedAt)}`
            : 'Каталог buyo обновляется каждый час'
        }
      />
      <OfferTabs active="/offers" />
      <div className="pc">
        {buyo && !buyo.connected ? (
          <div className="card">
            <EmptyState
              icon={<PlugZap />}
              title="Подключите buyo"
              action={
                <Link to="/settings" hash="buyo" className="btn btn-primary">
                  Открыть интеграции
                </Link>
              }
            >
              Каталог офферов с апрувом, CR, выплатами и холдом платформа берёт из кабинета buyo.
            </EmptyState>
          </div>
        ) : (
          <>
            {q.data && <OwnEconomics mine={q.data.mine} margin={q.data.margin ?? 0.3} />}
            {q.data?.signals && q.data.signals.length > 0 && (
              <Movers signals={q.data.signals} onPick={(ext) => setSearch(ext)} />
            )}
            <div className="catalog-tools">
              <h2 className="card-title">
                Каталог сети
                {q.data && <span className="count">{q.data.network.length}</span>}
              </h2>
              <FilterBar>
                <FilterSelect
                  label="Показать"
                  value={show}
                  onChange={setShow}
                  options={showOpts}
                  allValue="all"
                />
                <FilterSelect
                  label="Порядок"
                  value={sort}
                  onChange={setSort}
                  options={SORTS}
                  allValue="traffic"
                  icon={<ArrowUpDown aria-hidden />}
                  width={240}
                  showValue
                />
                <PeriodPicker value={period} onChange={setPeriod} label="Ваши лиды за период" />
                <ResetFilters shown={filtered || sort !== 'traffic'} onClick={reset} />
                <label className="search-field filter-end">
                  <Search aria-hidden />
                  <input
                    type="search"
                    placeholder="Найти оффер"
                    aria-label="Найти оффер"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </label>
              </FilterBar>
            </div>
            {q.isPending && (
              <div className="offer-grid">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="offer-card">
                    <Skeleton h={52} w={52} />
                    <Skeleton w="80%" />
                    <Skeleton h={34} />
                  </div>
                ))}
              </div>
            )}
            {q.isSuccess && q.data.network.length === 0 && (
              <div className="card">
                <EmptyState icon={<Package />} title="Каталог ещё загружается">
                  Первое обновление каталога buyo идёт в фоне — обычно это минута.
                </EmptyState>
              </div>
            )}
            {q.isSuccess && q.data.network.length > 0 && list.length === 0 && (
              <div className="card">
                <EmptyState
                  icon={<Search />}
                  title="Ничего не нашлось"
                  action={<Button onClick={reset}>Сбросить фильтры</Button>}
                >
                  {search.trim()
                    ? `По запросу «${search.trim()}» офферов нет${show !== 'all' ? ' среди выбранных' : ''}.`
                    : 'Под этот фильтр офферов нет.'}
                </EmptyState>
              </div>
            )}
            <div className="offer-grid">
              {list.map((o) => (
                <OfferCard
                  key={o.extId}
                  o={o}
                  phrase={phrase}
                  own={
                    q.data?.mine.find(
                      (m) => (o.trend?.ours && m.id === o.trend.ours.id) || m.networkOfferExtId === o.extId,
                    )?.economics ?? null
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}
